import { getFromDataPath, isDataSubset, useArsContext } from "aic";
import { Form } from "react-bootstrap";
import { NamedObjectControl } from "./NamedObjectControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";
import { WhereClauseManager } from "./WhereClauseManager";

/**
 * Props for the DataSubsetControl component.
 */
export interface DataSubsetControlProps {
  /** RFC 6901 JSON Pointer path to the DataSubset in ArsContext state */
  dataPath: string;
  /** Can rename the object */
  canRename?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * A component for managing DataSubset entities with full CRUD capabilities.
 *
 * This component provides:
 * - NamedObjectControl for editing name, description, and label
 * - WhereClauseManager for managing where clause conditions
 * - RemoveButton for deleting the data subset
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the DataSubset in ArsContext state.
 * @param props.canRename - Optional flag to allow renaming the object.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the data subset editor.
 */
export const DataSubsetControl = ({
  dataPath,
  canRename = false,
  disabled = false,
}: DataSubsetControlProps): React.ReactElement => {
  const { state } = useArsContext();
  const dataSubset = getFromDataPath(dataPath, state, isDataSubset);
  // Disable editing for parent templates
  if (dataSubset?.disableEditing) disabled = true;
  return (
    <div className="border rounded p-3 mb-3 position-relative">
      <div className="position-absolute bottom-0 end-0 m-1">
        <Form.Text>
          <small>Id: {dataPath.split("/").pop()}</small>
        </Form.Text>
      </div>
      {canRename && (
        <div className="position-absolute top-0 end-0 m-2">
          <RemoveArsItemButton
            dataPath={dataPath}
            disabled={disabled}
          />
        </div>
      )}
      <NamedObjectControl
        dataPath={dataPath}
        canRename={canRename}
        disabled={disabled}
      />
      <WhereClauseManager
        dataPath={dataPath}
        disabled={disabled}
      />
    </div>
  );
};

DataSubsetControl.displayName = "DataSubsetControl";
